import sort from 'fast-sort';
import { firebase } from 'src/firebase';
import DatabaseAdapter from 'src/firebase/db/DatabaseAdapter';
import { ReferenceError, InternalError } from 'src/firebase/db/ApplicationError';

/**
 * id: string | document_id
 * name: string | カテゴリ名
 * order: number | 表示順制御
 * is_featured: boolean | カテゴリ表示するか
 * created_at timestamp as firestore.FieldValue,
 * updated_at timestamp as firestore.FieldValue,
 */
export const bakedBreadTagInitialEntity = {
  id: '',
  name: '',
  order: 0,
  is_featured: false,
  is_disabled: false,
  created_at: {} as firebase.firestore.Timestamp,
  updated_at: {} as firebase.firestore.Timestamp,
};
export type BakedBreadTagEntity = typeof bakedBreadTagInitialEntity;
export const bakedBreadTagEntityKeys = (Object.keys(
  bakedBreadTagInitialEntity,
) as unknown) as (keyof BakedBreadTagEntity)[];

class BakedBreadTagsDB extends DatabaseAdapter {
  constructor(props: any) {
    super(props);
    this.setSchemaKeys(bakedBreadTagEntityKeys);
  }

  async getBakedBreadTags() {
    return this.getConnection()
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError('タグが存在しません');
        }
        const tags = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = await doc.data();
            return {
              ...data,
              id: doc.id,
            } as BakedBreadTagEntity;
          }),
        );
        // Firestoreのソートが効いてないので修正
        const results = sort(tags).by([{ asc: ({ order }) => order }]);
        return this.formatSuccess<BakedBreadTagEntity[]>(results);
      })
      .catch((err) => {
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  async create(query: Partial<BakedBreadTagEntity>) {
    const filteredQuery = this.filterQuery(query);
    const requestQuery = {
      ...filteredQuery,
      created_at: this.getServerTimeStamp(),
      updated_at: this.getServerTimeStamp(),
    };

    const ref = await this.getConnection().add(requestQuery);
    return await ref
      .update({ id: ref.id })
      .then(async () => {
        return this.formatSuccess({ ref });
      })
      .catch((err) => {
        throw new InternalError(err.message);
      });
  }

  async update(query: Partial<BakedBreadTagEntity>) {
    const filteredQuery = this.filterQuery(query);
    const requestQuery = {
      ...filteredQuery,
      updated_at: this.getServerTimeStamp(),
    };

    const ref = this.getDocumentRef(filteredQuery.id!);
    return ref
      .update(requestQuery)
      .then(async () => {
        const d = await this.getDocument(filteredQuery.id!);
        return this.formatSuccess(d);
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  async delete(id: string) {
    const docRef = this.getDocumentRef(id);
    const tag = (await (await docRef.get()).data()) as BakedBreadTagEntity;
    return docRef
      .delete()
      .then(async () => {
        return this.formatSuccess(tag);
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
  }
}

export default new BakedBreadTagsDB({
  schema: 'baked_bread_tags',
});
